import { NOTIFICATIONS_MAX_DEDUPE_CACHE_SIZE } from "../constants";
import { BroadcastEnvelope, NotificationItem, isNotificationDomainEvent } from "../types";
import { normalizeIncomingEnvelope } from "./normalizeIncomingEnvelope";

export type NotificationsState = {
  items: NotificationItem[];
  seenIds: Set<string>;
  seenOrder: string[];
};

export const createInitialNotificationsState = (): NotificationsState => ({
  items: [],
  seenIds: new Set<string>(),
  seenOrder: [],
});

const pushSeenId = (state: NotificationsState, id: string) => {
  if (state.seenIds.has(id)) {
    return;
  }

  state.seenIds.add(id);
  state.seenOrder.push(id);

  if (state.seenOrder.length > NOTIFICATIONS_MAX_DEDUPE_CACHE_SIZE) {
    const oldestSeenId = state.seenOrder.shift();
    if (oldestSeenId) {
      state.seenIds.delete(oldestSeenId);
    }
  }
};

export const addIncomingNotification = (
  state: NotificationsState,
  envelope: unknown,
  wireEventName?: string
): NotificationsState => {
  const normalized = normalizeIncomingEnvelope(envelope, wireEventName);
  if (!normalized) {
    return state;
  }

  if (state.seenIds.has(normalized.id)) {
    return state;
  }

  const nextState: NotificationsState = {
    items: [{ ...normalized, isRead: false }, ...state.items],
    seenIds: new Set(state.seenIds),
    seenOrder: [...state.seenOrder],
  };

  pushSeenId(nextState, normalized.id);
  return nextState;
};

export const markNotificationAsRead = (
  state: NotificationsState,
  id: string
): NotificationsState => ({
  ...state,
  items: state.items.map((item) =>
    item.id === id ? { ...item, isRead: true } : item
  ),
});

export const markAllNotificationsAsRead = (
  state: NotificationsState
): NotificationsState => ({
  ...state,
  items: state.items.map((item) => ({ ...item, isRead: true })),
});

export const getUnreadNotificationsCount = (state: NotificationsState) =>
  state.items.filter((item) => !item.isRead).length;

export const hydrateNotificationsFromItems = (
  items: NotificationItem[]
): NotificationsState => {
  const sorted = [...items]
    .filter((item) => isNotificationDomainEvent(item.eventName))
    .sort(
      (left, right) =>
        new Date(right.occurredAt).getTime() - new Date(left.occurredAt).getTime()
    );

  const nextState = createInitialNotificationsState();
  nextState.items = sorted;

  sorted.forEach((item) => {
    if (nextState.seenOrder.length < NOTIFICATIONS_MAX_DEDUPE_CACHE_SIZE) {
      pushSeenId(nextState, item.id);
    }
  });

  return nextState;
};
